import {ChevronRightIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import Title from './Title'
import Description from './Description'
import {Button, InlineEdit, ToggleSwitch, Custom} from './Controls'
import LinkLabel from './LinkLabel'
import Footer from './Footer'
import styles from './Item.module.css'
import {clsx} from 'clsx'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export type ControlGroupItemProps = {
  children: React.ReactNode
  nestedLevel?: 0 | 1 | 2
  disabled?: boolean
  contentsClassname?: string
  /** An icon rendered inside a styled icon wrapper */
  leadingIcon?: React.ReactNode
  /** A fully custom leading visual (no icon wrapper styling) */
  leadingVisual?: React.ReactNode
  /** When provided, makes the item's title a clickable link */
  href?: string
}

const slotConfig = {
  title: Title,
  description: Description,
  custom: Custom,
  toggle: ToggleSwitch,
  button: Button,
  inlineEdit: InlineEdit,
  linkLabel: LinkLabel,
  footer: Footer,
}

const Item = ({
  children,
  nestedLevel = 0,
  disabled = false,
  contentsClassname,
  leadingIcon = null,
  leadingVisual = null,
  href,
}: ControlGroupItemProps) => {
  const controlGroupUnificationEnabled = isFeatureEnabled('control-group-unification')
  const [{title, description, custom, toggle, button, inlineEdit, linkLabel, footer}] = useSlots(children, slotConfig)

  if (controlGroupUnificationEnabled) {
    const blockControl = button || inlineEdit || custom || toggle || linkLabel

    return (
      <>
        <div
          className={clsx(
            'controlBoxContainer',
            styles.container,
            disabled && styles.disabled,
            href && styles.link,
            href && !blockControl && styles.linkWithoutControl,
          )}
          style={{'--nested-level': nestedLevel}}
        >
          <div className={clsx(styles.contents, contentsClassname)}>
            {leadingIcon || leadingVisual ? (
              leadingVisual ? (
                <div className={styles.leadingVisual}>{leadingVisual}</div>
              ) : (
                <div className={`${styles.leadingVisual} ${styles.blockIcon}`}>
                  <div className={styles.leadingIcon}>{leadingIcon}</div>
                </div>
              )
            ) : null}
            {href && title ? (
              <Link href={href} className={clsx(styles.title, styles.itemLink)}>
                {title}
              </Link>
            ) : (
              title && <div className={clsx(styles.title, 'titleBox')}>{title}</div>
            )}
            {description && <div className={clsx(styles.description, 'descriptionBox')}>{description}</div>}
            {blockControl && <div className={clsx(styles.blockControl, 'blockControl')}>{blockControl}</div>}
            {href && !blockControl ? (
              <div className={styles.trailingVisual}>
                <span className="d-flex fgColor-muted flex-items-center align-self-center height-full flex-justify-center">
                  <ChevronRightIcon />
                </span>
              </div>
            ) : undefined}
          </div>
        </div>
        {footer && <div className={clsx(styles.footer, 'footer')}>{footer}</div>}
      </>
    )
  } else {
    const blockControl = button || inlineEdit || custom || toggle

    return (
      <div
        className={clsx('controlBoxContainer', styles.container, disabled && styles.disabled)}
        style={{'--nested-level': nestedLevel}}
      >
        <div className={clsx(styles.contents, contentsClassname)}>
          {title && <div className={clsx(styles.title, 'titleBox')}>{title}</div>}
          {description && <div className={clsx(styles.description, 'descriptionBox')}>{description}</div>}
          {blockControl && <div className={clsx(styles.blockControl, 'blockControl')}>{blockControl}</div>}
        </div>
      </div>
    )
  }
}

export default Item
